import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Col } from 'react-bootstrap';

function ProgressBar({ pages, pageNumber, featureFlags }) {
  const containerRef = useRef(null);
  const [lineStyle, setLineStyle] = useState({});

  // Consistent mobile progress bar color - Context7 standard
  const activeColor = featureFlags && featureFlags.progressBarActiveColor
    ? featureFlags.progressBarActiveColor
    : '#007bff'; // Bootstrap primary blue for consistency

  // Mobile-optimized active badge style
  const activeBadgeStyle = {
    backgroundColor: activeColor,
    color: '#fff',
    border: `2px solid ${activeColor}`,
    borderRadius: '18px',
    padding: '6px 14px',
    fontWeight: '600',
    fontSize: '0.875rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
  };

  // Mobile-optimized inactive badge style
  const inactiveBadgeStyle = {
    backgroundColor: '#f8f9fa',
    color: '#6c757d',
    border: '2px solid #dee2e6',
    borderRadius: '18px',
    padding: '6px 14px',
    fontWeight: '500',
    fontSize: '0.875rem',
    transition: 'all 0.2s ease',
  };

  const recalcLine = useCallback(() => {
    if (!containerRef.current) return;
    const badges = containerRef.current.querySelectorAll('.progress-badge');
    if (!badges.length || badges.length < 2) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const topPositions = Array.from(badges).map(
      (badge) => badge.getBoundingClientRect().top
    );
    const firstTop = topPositions[0];

    const allSameRow = topPositions.every((top) => Math.abs(top - firstTop) < 10);
    if (!allSameRow) {
      setLineStyle({ display: 'none' });
      return;
    }

    // Get badge positions dynamically
    const firstBadge = badges[0];
    const lastBadge = badges[badges.length - 1];
    const firstRect = firstBadge.getBoundingClientRect();
    const lastRect = lastBadge.getBoundingClientRect();

    const left = firstRect.left - containerRect.left + firstRect.width / 2;
    const right = lastRect.left - containerRect.left + lastRect.width / 2;
    const width = Math.max(0, right - left);

    // Calculate progress percentage for the line
    const totalSteps = pages.length - 1; // Number of gaps between badges
    const completedSteps = Math.max(0, Math.min(pageNumber - 1, totalSteps));
    const progressPercentage = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

    setLineStyle({
      position: 'absolute',
      top: '50%',
      left: `${left}px`,
      width: `${width}px`,
      height: '3px',
      background: `linear-gradient(to right, ${activeColor} ${progressPercentage}%, #dee2e6 ${progressPercentage}%)`,
      transform: 'translateY(-50%)',
      zIndex: 0,
      display: 'block',
    });
  }, [pages, pageNumber, activeColor]);

  useEffect(() => {
    recalcLine();
    window.addEventListener('resize', recalcLine);
    return () => window.removeEventListener('resize', recalcLine);
  }, [recalcLine]);

  return (
    <Col style={{ width: '100%' }}>
      <div
        ref={containerRef}
        className="progress-container"
        style={{
          position: 'relative',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '30px',
          padding: '20px',
          boxSizing: 'border-box',
          minHeight: '60px',
        }}
      >
        <div style={lineStyle} />

        {pages.map((p, i) => (
          <div
            key={`badge-${i}`}
            className="progress-badge"
            style={{ position: 'relative', zIndex: 1 }}
          >
            <span style={i < pageNumber ? activeBadgeStyle : inactiveBadgeStyle}>
              {p}
            </span>
          </div>
        ))}
      </div>
    </Col>
  );
}

export default ProgressBar;
